import { useState } from 'react';
import { Button, Card, Field, FieldGrid, toast } from '../../ui';
import { PrinterSwitch } from './PrinterSwitch';
import { StatusBadge } from './StatusBadge';
import {
  apiErrorMessage,
  formatDate,
  printerStatus,
  stationStatus,
} from './status';
import type {
  ShipNodePrinter,
  ShipNodeStation,
  ShipNodeStationsService,
} from './types';

type PrinterRowProps = {
  service: ShipNodeStationsService;
  station: ShipNodeStation;
  printer: ShipNodePrinter;
  canUse: boolean;
  onChanged: () => void;
};

function PrinterRow({
  service,
  station,
  printer,
  canUse,
  onChanged,
}: PrinterRowProps) {
  /* What the switch shows while its PUT is in flight. */
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);
  const [isSending, setIsSending] = useState(false);

  const setEnabled = async (enabled: boolean) => {
    setPendingEnabled(enabled);
    try {
      await service.setPrinterEnabled(station.id, printer.id, enabled);
      await onChanged();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not change the printer'));
    } finally {
      setPendingEnabled(null);
    }
  };

  const sendTestPage = async () => {
    setIsSending(true);
    try {
      await service.sendTestPage(station.id, printer.id);
      toast.success(`Test page sent to ${printer.displayName}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not send the test page'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <li className="sk:flex sk:flex-wrap sk:items-center sk:gap-x-4 sk:gap-y-2 sk:border-t sk:border-rule sk:py-3 sk:first:border-t-0">
      <PrinterSwitch
        isSelected={pendingEnabled ?? printer.enabled}
        onChange={setEnabled}
        isDisabled={!canUse || pendingEnabled !== null}
        aria-label={`Use ${printer.displayName}`}
      />
      <div className="sk:flex sk:min-w-0 sk:flex-1 sk:flex-col sk:gap-1">
        <span className="sk:truncate sk:text-dense sk:font-medium sk:text-fg">
          {printer.displayName}
        </span>
        <StatusBadge status={printerStatus(printer)} />
      </div>
      <Button
        size="sm"
        onPress={sendTestPage}
        isDisabled={!canUse || isSending}
      >
        {isSending ? 'Sending…' : 'Send test page'}
      </Button>
    </li>
  );
}

type StationCardProps = {
  service: ShipNodeStationsService;
  station: ShipNodeStation;
  /** Remove station: the portal limits it to admins (the API's requireOrgAdmin); the WMS to nobody. */
  canManage: boolean;
  /** Printer switch and test page: everyone but view-only users. */
  canUse: boolean;
  onRemove: (station: ShipNodeStation) => void;
  onChanged: () => void;
};

export function StationCard({
  service,
  station,
  canManage,
  canUse,
  onRemove,
  onChanged,
}: StationCardProps) {
  const name = station.name || 'Unnamed station';

  return (
    <Card title={name} action={<StatusBadge status={stationStatus(station)} />}>
      <div className="sk:flex sk:flex-col sk:gap-4">
        <FieldGrid>
          <Field label="System">{station.os || '—'}</Field>
          <Field label="ShipNode version">{station.appVersion || '—'}</Field>
          <Field label="Paired">{formatDate(station.pairedAt)}</Field>
        </FieldGrid>

        <div>
          <h3 className="sk:m-0 sk:mb-1 sk:text-xs sk:font-medium sk:text-fg-tertiary">
            Printers
          </h3>
          {station.printers.length ? (
            <ul className="sk:m-0 sk:list-none sk:p-0">
              {station.printers.map(printer => (
                <PrinterRow
                  key={printer.id}
                  service={service}
                  station={station}
                  printer={printer}
                  canUse={canUse}
                  onChanged={onChanged}
                />
              ))}
            </ul>
          ) : (
            <p className="sk:m-0 sk:text-dense sk:text-fg-tertiary">
              This station hasn't reported any printers yet.
            </p>
          )}
        </div>

        {canManage ? (
          <div className="sk:flex sk:justify-end">
            <Button
              variant="danger"
              size="sm"
              onPress={() => onRemove(station)}
            >
              Remove station
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
